import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { PiLinkLight } from 'react-icons/pi';
import { useAlert } from 'src/contexts/alert-context';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useUnsavedNavigationGuard } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import {
  useInitFacebookSDK,
  useStoreWhatsAppIntegration,
  useWhatsAppSignup,
} from 'src/hooks/integrations/facebook';
import type { WhatsAppSignupFailureReason } from 'src/hooks/integrations/facebook/types';
import { IntegrationCard } from '../../molecules/integrations/integration-card';
import { IntegrationBrandAvatar } from '../../molecules/integrations/integration-brand-avatar';
import { WhatsAppSignupProgressModal } from '../../molecules/integrations/whatsapp-signup-progress-modal';
import { useCreateWhatsAppOnboardingConfiguration } from 'src/modules/shared/hooks/api/messaging/whatsapp/integrations';
import { ComosManagedWhatsAppOnboardingConfigurationResponse } from 'src/api/generated';
import type { WhatsAppIntegration } from 'src/api/generated';
import { classifyWhatsAppIntegrationOnboardingSafety } from 'src/modules/shared/utils/integrations/whatsapp-integration-onboarding-safety.util';
import { signupFailureAlerts } from '../../../constants/whatsapp-signup.constants';
import { WhatsAppBillingPolicyControl } from './whatsapp-billing-policy-control';

/** Coordinates signup and backend completion for a user allowed to connect accounts. */
export const WhatsAppIntegrationCardEnabled: React.FC = () => {
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { t } = useI18n();
  const { showAlert } = useAlert();
  const {
    createWhatsAppOnboardingConfiguration,
    loading: preparingOnboarding,
    error: onboardingConfigurationError,
  } = useCreateWhatsAppOnboardingConfiguration();

  const handleSignupFailure = useCallback(
    (reason: WhatsAppSignupFailureReason) => {
      const alert = signupFailureAlerts[reason];
      showAlert(t(alert.translationKey), alert.type);
    },
    [showAlert, t]
  );
  const handleFinalizationSuccess = useCallback(
    (storedIntegration: WhatsAppIntegration) => {
      const { isUnsafe } =
        classifyWhatsAppIntegrationOnboardingSafety(storedIntegration);
      showAlert(
        t(
          isUnsafe
            ? 'settings.integrations.whatsapp.connection.quarantinedSaved'
            : 'settings.integrations.whatsapp.connection.created'
        ),
        isUnsafe ? 'warning' : 'success'
      );
    },
    [showAlert, t]
  );
  const handleFinalizationError = useCallback(() => {
    showAlert(t('settings.integrations.whatsapp.errors.finalization'), 'error');
  }, [showAlert, t]);

  useInitFacebookSDK();
  const {
    signupInProgress,
    signupResult,
    launchWhatsAppSignup,
    cancelWhatsAppSignup,
  } = useWhatsAppSignup({
    onSignupFailure: handleSignupFailure,
  });
  const { loading, error, integration } = useStoreWhatsAppIntegration({
    signupResult,
    onSuccess: handleFinalizationSuccess,
    onError: handleFinalizationError,
  });
  const isFinalizing = Boolean(signupResult) && !integration && !error;
  const onboardingInProgress = signupInProgress || isFinalizing;
  const shouldBlockNavigation = preparingOnboarding || onboardingInProgress;
  const { isBlocked: isNavigationBlocked, stay: stayOnPage } =
    useUnsavedNavigationGuard(shouldBlockNavigation);

  useEffect(() => {
    if (isNavigationBlocked) {
      stayOnPage();
    }
  }, [isNavigationBlocked, stayOnPage]);

  const integrationSummary = useMemo(() => {
    if (!integration) return null;
    return {
      providerName: integration.providerName,
      providerId: integration.providerId,
      activatedAt: integration.activatedAt,
    };
  }, [integration]);

  const handleConnect = useCallback(async () => {
    try {
      const configuration = await createWhatsAppOnboardingConfiguration();
      if (!isMountedRef.current) return;

      launchWhatsAppSignup({
        attemptId: configuration.attemptId,
        billingMode: configuration.billingMode,
        solutionId:
          configuration.billingMode ===
          ComosManagedWhatsAppOnboardingConfigurationResponse.billingMode
            .COMOS_MANAGED
            ? configuration.solutionId
            : undefined,
      });
    } catch {
      if (!isMountedRef.current) return;

      showAlert(
        t('settings.integrations.whatsapp.errors.onboardingConfiguration'),
        'error'
      );
    }
  }, [
    createWhatsAppOnboardingConfiguration,
    launchWhatsAppSignup,
    showAlert,
    t,
  ]);

  const onboardingSafety = useMemo(
    () =>
      integration
        ? classifyWhatsAppIntegrationOnboardingSafety(integration)
        : null,
    [integration]
  );
  const isQuarantined = onboardingSafety?.isUnsafe ?? false;
  const showManagedFundingWarning =
    Boolean(onboardingSafety) &&
    !isQuarantined &&
    onboardingSafety?.isComosManaged;

  return (
    <>
      <IntegrationCard
        title={t('settings.integrations.whatsapp.title')}
        subtitle={t('settings.integrations.whatsapp.description')}
        cardProps={{ shadow: 'md', variant: 'primary', appearance: 'outline' }}
        icon={<IntegrationBrandAvatar integrationId="whatsapp" />}
        rightAction={
          <Button
            variant="primary"
            onClick={() => void handleConnect()}
            loading={loading || preparingOnboarding}
            disabled={onboardingInProgress || preparingOnboarding}
            className="flex items-center gap-2 !bg-[#1877f2] hover:!bg-[#166fe5] active:!bg-[#165cbe] !text-white"
          >
            <PiLinkLight className="w-4 h-4" />
            <span className="hidden xl:inline">
              {t('settings.integrations.whatsapp.actions.connect')}
            </span>
            <span className="xl:hidden">
              {t('settings.integrations.whatsapp.actions.connectShort')}
            </span>
          </Button>
        }
      >
        <div className="space-y-4">
          <Alert
            variant="warning"
            size="md"
            message={t('settings.integrations.whatsapp.warning.keepTabOpen')}
          />

          <WhatsAppBillingPolicyControl />

          {onboardingConfigurationError ? (
            <Alert
              role="alert"
              size="md"
              variant="danger"
              message={t(
                'settings.integrations.whatsapp.errors.onboardingConfiguration'
              )}
            />
          ) : null}

          {error ? (
            <Alert
              size="md"
              variant="danger"
              message={t('settings.integrations.whatsapp.errors.finalization')}
            />
          ) : null}

          {integrationSummary ? (
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3 space-y-1">
              <Text size="small" weight="medium">
                {t('settings.integrations.whatsapp.connection.linked')}
              </Text>
              <Text size="small" color="secondary">
                {t('settings.integrations.whatsapp.connection.provider')}:{' '}
                {integrationSummary.providerName}
              </Text>
              <Text size="small" color="secondary">
                {t('settings.integrations.whatsapp.connection.id')}:{' '}
                {integrationSummary.providerId}
              </Text>
              <Text size="small" color="secondary">
                {t('settings.integrations.whatsapp.connection.activatedAt')}:{' '}
                {formatDate(integrationSummary.activatedAt)}
              </Text>
            </div>
          ) : null}

          {isQuarantined ? (
            <Alert
              role="alert"
              size="md"
              variant="warning"
              message={t(
                'settings.integrations.whatsapp.warning.solutionAssociationVerification'
              )}
            />
          ) : null}

          {showManagedFundingWarning ? (
            <Alert
              role="alert"
              size="md"
              variant="warning"
              message={t(
                'settings.integrations.whatsapp.warning.managedFunding'
              )}
            />
          ) : null}
        </div>
      </IntegrationCard>

      <WhatsAppSignupProgressModal
        isOpen={onboardingInProgress}
        phase={signupInProgress ? 'meta' : 'finalizing'}
        onCancel={cancelWhatsAppSignup}
      />
    </>
  );
};
